import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, UserPlus, FileEdit } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '@/services/api';

const EMPTY = { name: '', student_id: '', date_of_birth: '', year_level: '', stream: '', parent_name: '', parent_phone: '' };

const fromStudent = (s) => {
  const m = (s?.grade || '').match(/Year\s*(\d+)\s*(.*)$/i);
  return {
    name: s?.name || '', student_id: s?.studentNumber || '', date_of_birth: s?.dateOfBirth || '',
    year_level: m ? m[1] : '', stream: m ? (m[2] || '').trim() : '',
    parent_name: s?.parentName || '', parent_phone: s?.parentPhone || '',
  };
};

export const AddStudentDialog = ({ open, onOpenChange, years, onCreated, student = null }) => {
  const isEdit = !!student;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (open) setForm(isEdit ? fromStudent(student) : EMPTY); }, [open, student, isEdit]);

  const submit = async () => {
    if (!form.name.trim() || !form.year_level) {
      toast.error('Student name and year are required');
      return;
    }
    setSaving(true);
    const payload = { name: form.name.trim(), year_level: Number(form.year_level), stream: form.stream || null,
      student_id: form.student_id || null, date_of_birth: form.date_of_birth || null,
      parent_name: form.parent_name || null, parent_phone: form.parent_phone || null };
    try {
      if (isEdit) {
        await apiService.updateStudent(student.id, { ...payload, student_id: form.student_id, date_of_birth: form.date_of_birth, stream: form.stream, parent_name: form.parent_name, parent_phone: form.parent_phone });
        toast.success(`${payload.name} updated`);
      } else {
        await apiService.createStudent(payload);
        toast.success(`${payload.name} added to Year ${form.year_level}`);
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.message || (isEdit ? 'Failed to update student' : 'Failed to add student'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={isEdit ? 'edit-student-dialog' : 'add-student-dialog'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <FileEdit className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />} {isEdit ? 'Edit Student' : 'Add Student'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Correct the student\'s details. Changing the year moves them to that year group.' : 'Add one student to your school. Parent phone enables SMS report-card links.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Full name *</Label>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. Jane Tari" data-testid="add-student-name" />
          </div>
          <div>
            <Label>Student ID</Label>
            <Input value={form.student_id} onChange={set('student_id')} placeholder="School ID number" data-testid="add-student-id" />
          </div>
          <div>
            <Label>Date of birth</Label>
            <Input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} data-testid="add-student-dob" />
          </div>
          <div>
            <Label>Year *</Label>
            <select value={form.year_level} onChange={set('year_level')} className="w-full h-10 border rounded-md px-3 bg-background text-sm" data-testid="add-student-year">
              <option value="">Select year…</option>
              {years.map((y) => <option key={y} value={y}>Year {y}</option>)}
            </select>
          </div>
          <div>
            <Label>Stream (optional)</Label>
            <Input value={form.stream} onChange={set('stream')} placeholder="A, B, Science, Arts…" data-testid="add-student-stream" />
          </div>
          <div>
            <Label>Parent / guardian name</Label>
            <Input value={form.parent_name} onChange={set('parent_name')} data-testid="add-student-parent-name" />
          </div>
          <div>
            <Label>Parent phone</Label>
            <Input value={form.parent_phone} onChange={set('parent_phone')} placeholder="e.g. 7771234" data-testid="add-student-parent-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-student-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="add-student-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {isEdit ? 'Save Changes' : 'Add Student'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
